/* eslint-disable react/no-danger */
import tw from 'twin.macro'
import React, { useRef } from 'react'
import { css } from '@emotion/react'

import Collapsible from '@/components/ui/collapsible'
import { slugify } from '@/utils/strings'
import { BwTableTooltip, BwTableTooltipTrigger, BwTableTooltipContent } from '@/components/ui/tooltip'
import useStickyHelper from '@/utils/useStickyHelper'

type ComparisonGridProps = {
  data: {
    content: TableData | string
  }
  textAlign?: 'Left' | 'Center'
  isPrintView?: boolean
}

export type TableRowData = {
  subheader?: string
  Tooltip?: React.ReactNode
  cells: (
    | React.ReactNode
    | {
        linkUrl: string
        linkText: string
        text: string
        icon: string
        subtext: string
      }
  )[]
}

export type TableData = {
  id?: string
  tableTitle: string
  subtext?: React.ReactNode
  headers?: React.ReactNode[]
  body: TableRowData[]
  footer?: TableRowData[]
}

function ExpandIcon({ className = '', isExpanded, openIcon = '&#xe921;', closeIcon = '&#xe925;' }) {
  const iconStyle = tw`absolute inset-0 m-auto transition-[transform,opacity] w-full h-full origin-center leading-[100%]`
  return (
    <span className={className} css={[tw`relative inline-block font-bwi text-indigoBlue`]}>
      <span
        css={[
          iconStyle,
          isExpanded ? tw`opacity-100 rotate-0` : tw`opacity-0 -rotate-90`,
          closeIcon === '&#xe925;' && tw`translate-x-[2px]`, // minus-circle icon is off-center
        ]}
        dangerouslySetInnerHTML={{ __html: closeIcon }}
      />
      <span
        css={[iconStyle, !isExpanded ? tw`opacity-100 rotate-0` : tw`opacity-0 rotate-90`]}
        dangerouslySetInnerHTML={{ __html: openIcon }}
      />
    </span>
  )
}

export default function ComparisonGrid({ data, textAlign = 'Center', isPrintView }: ComparisonGridProps) {
  const tableData: TableData | undefined = typeof data.content === 'object' ? data.content : JSON.parse(data.content)

  const containerRef = useRef<HTMLDivElement>(null)
  const { stickyTracker, top, isStuck, isFloating } = useStickyHelper({ containerRef })

  const columns = tableData?.headers?.length ?? 0
  const hasFooter = tableData?.footer?.length > 0

  const lastRowStyles = (column) => [
    tw`bg-white z-1`,
    column === 0 ? tw`col-span-full md:(col-auto border-r rounded-bl-3xl)` : tw`md:border-r`,
    column === 1 && tw`rounded-bl-3xl md:rounded-none`,
    column === columns - 1 && tw`border-r-0 rounded-br-3xl`,
  ]

  const headerRef = useRef<HTMLDivElement>(null)
  const headerHeight = headerRef.current?.offsetHeight || 0
  const textAlignStyle =
    !textAlign || textAlign === 'Center' ? tw`text-center text-balance` : tw`text-center text-balance md:text-left`

  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({
    // Expand the first 3 rows by default
    0: true,
    1: true,
    2: true,
  })
  const isSomeExpanded = Object.keys(expanded).some((key) => expanded[key])
  const toggleAllRows = () => {
    if (isSomeExpanded) {
      // Collapse all
      setExpanded({})
    } else {
      // Expand all
      setExpanded(tableData?.body.reduce((acc, _, index) => ({ ...acc, [index]: true }), {}) as Record<number, boolean>)
    }
  }

  return (
    <section
      id={tableData.id ? slugify(tableData.id) : undefined}
      ref={containerRef}
      className="comparison-grid"
      tw="bg-gray-100 relative [.rich-icon]:(ml-1 text-[20px] sm:text-[25px] text-primaryBlue) [a]:no-underline [p:last-child]:mb-0"
      css={[isPrintView && tw`bg-white`]}
    >
      {/* Mask for hiding grid above sticky header */}
      <div
        tw="sticky left-0 z-[2] w-full pointer-events-none"
        style={{
          opacity: isStuck ? 1 : 0,
          top: `${top + headerHeight / 2}px`,
        }}
      >
        <div
          tw="absolute top-0 left-0 z-10 w-full from-gray-100 to-transparent bg-gradient-to-b from-90%"
          style={{
            height: `${top + headerHeight / 2}px`,
            top: `-${top + headerHeight / 2}px`,
          }}
        />
      </div>
      <div tw="mx-auto max-w-8xl px-5 py-5 md:(px-9 py-20)" css={isPrintView && tw`px-0! max-w-none`}>
        <h3 tw="relative z-[3] text-center" css={tableData.subtext ? tw`mb-4` : tw`mb-10`}>
          {tableData.tableTitle}
        </h3>
        {tableData.subtext && <div tw="relative z-[3] text-center mb-10">{tableData.subtext}</div>}
        <div
          tw="grid relative -mx-5 sm:mx-0"
          css={[
            css`
              grid-template-columns: repeat(${columns - 1}, minmax(0, 1fr));

              @media (min-width: 768px) {
                grid-template-columns: repeat(${columns}, minmax(0, 1fr));
              }
            `,
            isPrintView && tw`border`,
          ]}
        >
          {stickyTracker}
          {/* Floating shadow element */}
          <div
            tw="absolute bottom-0 left-0 z-0 w-full m-auto shadow-lg rounded-3xl overflow-hidden"
            css={[isStuck ? tw`h-[calc(100% - 70px)]` : tw`h-[calc(100% - 15px)]`, isPrintView && tw`shadow-none`]}
          />
          {/* Headers */}
          {tableData?.headers?.map((header, indexColumn) => {
            const col = `column${indexColumn + 1}`
            return (
              <div
                key={indexColumn}
                ref={indexColumn === 0 ? headerRef : undefined}
                css={[
                  tw`flex w-full justify-between [p]:mb-0 pt-5 pb-3 pl-3 pr-4 sm:px-4 text-xl font-bold bg-white`,
                  tw`md:(sticky z-10 text-lg pt-4 pb-4 border-r shadow-sm)`,
                  indexColumn === 0
                    ? tw`sticky z-10 text-left col-span-full sm:rounded-t-3xl md:(rounded-tr-none col-auto) lg:(px-14 text-lg)`
                    : [tw`hidden md:block`, textAlignStyle],
                  indexColumn === columns - 1 && tw`md:(rounded-tr-3xl border-r-0)`,
                  isStuck && !isFloating && tw`rounded-none! border-b-[1px]`,
                  isStuck &&
                    isFloating && [
                      tw`shadow-lg border border-l-0 border-b-[1px]`,
                      indexColumn === 0 && tw`rounded-tl-3xl rounded-tr-3xl md:rounded-tr-none border-l`,
                      indexColumn === columns - 1 && tw`rounded-tr-3xl border-l-0`,
                    ],
                  isPrintView && tw`!pt-3 !pb-3 [&>*]:!pb-0 shadow-none! border-b-[1px]`,
                ]}
                style={{
                  top: `${top}px`,
                }}
              >
                {header?.[col] || header}
                {indexColumn === 0 && (
                  <button
                    type="button"
                    tw="md:hidden translate-x-2 text-[1.188rem] w-[1.188rem] h-[1.188rem] p-1 box-content"
                    onClick={toggleAllRows}
                    aria-label={isSomeExpanded ? 'Collapse all' : 'Expand all'}
                  >
                    <ExpandIcon
                      tw="w-[1.188rem] h-[1.188rem]"
                      isExpanded={isSomeExpanded}
                      openIcon="&#xe934;"
                      closeIcon="&#xe93c;"
                    />
                  </button>
                )}
              </div>
            )
          })}
          {/* Body */}
          {tableData?.body.map((row, indexRow, rows) =>
            row.subheader ? (
              <div key={indexRow} tw="px-4 py-7 col-span-full border-b font-bold bg-white z-1 lg:(px-14 text-lg)">
                <span>{row.subheader}</span>
              </div>
            ) : (
              tableData.headers.map((header, indexColumn) => {
                const col = `column${indexColumn + 1}`
                const isLastRow = rows.length - 1 - indexRow === 0
                const cell = row?.cells?.[indexColumn] || row[col]
                const isExpanded = expanded[indexRow]
                const content =
                  indexColumn === 0 ? ( // First header cell
                    <div tw="flex items-center content-between gap-2">
                      {/* Feature heading */}
                      <div tw="flex-1">
                        {cell?.linkUrl ? (
                          <>
                            <a tw="flex items-center" href={cell.linkUrl}>
                              {cell.text}
                              {cell.icon === 'iconLink' && (
                                <span tw="font-bwi ml-1 text-[18px] text-primaryBlue">&#xe91b;</span>
                              )}
                            </a>
                            {cell.subtext && <span tw="text-xs">{cell.subtext}</span>}
                          </>
                        ) : (
                          <span
                            tw="[a::after]:(content-['&#xe91b;'] font-bwi ml-1 text-[18px] text-primaryBlue)"
                            css={isPrintView && tw`[a::after]:hidden`}
                          >
                            {cell}
                          </span>
                        )}
                      </div>
                      <ExpandIcon tw="md:hidden w-4 h-4 text-base" isExpanded={isExpanded} />
                    </div>
                  ) : // Body cell
                  cell?.linkText ? (
                    <>
                      <a tw="text-primaryBlue" href={cell.linkUrl}>
                        {cell.linkText}
                      </a>
                      <br />
                      {cell.text}
                    </>
                  ) : cell === 'iconTick' ? (
                    <span tw="font-bwi text-[25px] text-primaryBlue">&#xe912;</span>
                  ) : typeof cell === 'string' ? (
                    <span dangerouslySetInnerHTML={{ __html: cell }} />
                  ) : (
                    cell
                  )
                return (
                  <div
                    key={indexColumn}
                    css={[
                      !hasFooter && isLastRow
                        ? lastRowStyles(indexColumn)
                        : [
                            tw`bg-white z-1 md:border-b`,
                            indexColumn === 0 ? tw`col-span-full md:(col-auto border-r)` : tw`border-b md:border-r`,
                            indexColumn === columns - 1 && tw`border-r-0`,
                          ],
                    ]}
                  >
                    {col === 'column1' ? (
                      <>
                        {/* Desktop */}
                        <div tw="relative w-full h-full [button]:(w-full h-full p-4 z-1) hidden md:block">
                          {row.Tooltip && !isPrintView && (
                            <BwTableTooltip>
                              <BwTableTooltipTrigger>{content}</BwTableTooltipTrigger>
                              <BwTableTooltipContent css={tw`[p:last-child]:mb-0`}>{row.Tooltip}</BwTableTooltipContent>
                            </BwTableTooltip>
                          )}
                          {/* Sometimes the tooltip button is absolute and ignores the height of the content */}
                          {/* This div is a hidden placeholder to fill the height of the cell */}
                          <div
                            css={[
                              tw`hidden leading-tight text-base h-full md:(flex p-4) lg:(text-lg px-14 py-6)`,
                              row.Tooltip && tw`opacity-0 z-0`,
                              isPrintView && tw`block opacity-100 py-3!`,
                            ]}
                          >
                            {content}
                          </div>
                        </div>
                        {/* Mobile */}
                        <button
                          type="button"
                          css={[
                            tw`w-full px-3 py-4 text-[14px] sm:(p-4 text-base) text-left md:hidden`,
                            !isExpanded && indexColumn > 0 && tw`hidden`,
                          ]}
                          onClick={(e) => {
                            const target = e.target as HTMLElement
                            if (target && !isExpanded) {
                              setTimeout(() => {
                                target.scrollIntoView({ behavior: 'smooth', block: 'center' })
                              }, 100)
                            }
                            setExpanded({ ...expanded, [indexRow]: !isExpanded })
                          }}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          <div tw="font-semibold">{content}</div>
                        </button>
                        {row.Tooltip && (
                          <Collapsible tw="md:(h-auto! opacity-100!)" isOpen={isExpanded}>
                            <div css={[tw`md:hidden text-sm text-gray-600 pt-1.5 px-2 sm:px-4 pb-4`]}>
                              {row.Tooltip && row.Tooltip}
                            </div>
                          </Collapsible>
                        )}
                      </>
                    ) : (
                      <Collapsible tw="md:(h-auto! opacity-100!)" isOpen={isExpanded}>
                        <div css={[tw`text-[13px] p-3 pb-4 sm:(p-4 text-base)`, textAlignStyle]}>
                          <div tw="pb-2 [&_strong]:font-medium font-medium sm:[&_strong]:font-semibold! sm:font-semibold md:hidden">
                            {header?.[col] || header}
                          </div>
                          {content}
                        </div>
                      </Collapsible>
                    )}
                  </div>
                )
              })
            )
          )}
          {/* Footer */}
          {tableData?.footer?.map((row, indexRow) =>
            row.subheader ? (
              <div key={indexRow} tw="col-span-full px-4 py-7 md:px-12 border-b font-bold z-1">
                {row.subheader}
              </div>
            ) : (
              tableData.headers.map((header, indexColumn) => {
                const col = `column${indexColumn + 1}`
                const cell = row?.cells?.[indexColumn] || row[col]
                return (
                  <div
                    key={indexColumn}
                    css={[
                      ...lastRowStyles(indexColumn),
                      tw`pt-4 pb-11 px-4 font-bold lg:text-lg`,
                      indexColumn === 0 && tw`pb-4 md:pb-11 lg:px-14`,
                    ]}
                  >
                    {col === 'column1' ? (
                      cell
                    ) : (
                      <div css={textAlignStyle}>
                        <div tw="pb-2 font-semibold md:hidden">{header?.[col] || header}</div>
                        {cell?.text ? (
                          <>
                            <span dangerouslySetInnerHTML={{ __html: cell.text }} />
                            <br />
                            {cell.linkText && (
                              <a tw="text-primaryBlue" href={cell.linkUrl}>
                                {cell.linkText}
                              </a>
                            )}
                          </>
                        ) : (
                          cell
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )
          )}
        </div>
      </div>
    </section>
  )
}
